import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "ball"]

  connect() {
    this.renderRetryCount = 0
    this.renderTimer = null
    this.refreshTimer = null
    this.widgetId = null
    this.boundTurnstileSuccess = (token) => this.onSuccess(token)
    this.boundTurnstileExpired = () => this.onExpired()
    this.boundTurnstileError = (...args) => this.onError(...args)
    this.prepareWidgetForRender()
    this.hideLoginBallUntilVerified()
    this.scheduleTurnstileRender(100)
  }

  disconnect() {
    this.cancelRefresh()
    if (this.renderTimer) {
      window.clearTimeout(this.renderTimer)
      this.renderTimer = null
    }

    if (this.widgetId !== null && typeof turnstile !== "undefined" && typeof turnstile.remove === "function") {
      try {
        turnstile.remove(this.widgetId)
      } catch (error) {
        console.warn("❌ Turnstile cleanup skipped:", error)
      }
    }

    const widget = this.widgetElement()
    if (widget) {
      delete widget.dataset.rendered
      delete widget.dataset.pinballRenderPending
    }
    this.widgetId = null
  }

  hideLoginBallUntilVerified() {
    if (!this.hasBallTarget) return
    this.ballTarget.disabled = false
    this.ballTarget.style.opacity = "1"
    this.ballTarget.style.visibility = "visible"
    this.ballTarget.style.pointerEvents = "auto"
  }

  widgetElement() {
    return this.element.querySelector(".cf-turnstile")
  }

  currentForm() {
    return this.element.tagName === "FORM" ? this.element : this.element.closest("form")
  }

  currentPath() {
    return window.location.pathname.toString()
  }

  airdropFlow() {
    const formAction = this.currentForm()?.getAttribute("action").toString() || ""
    return this.currentPath().startsWith("/airdrops") || formAction.includes("/airdrops")
  }

  resetHiddenTokens() {
    const form = this.currentForm()
    const hiddenInForm = form ? form.querySelectorAll('input[name="cf-turnstile-response"]') : []
    hiddenInForm.forEach((input) => { input.value = "" })
  }

  prepareWidgetForRender() {
    this.resetHiddenTokens()
    const widget = this.widgetElement()
    if (!widget) return

    delete widget.dataset.rendered
    delete widget.dataset.pinballRenderPending
    widget.replaceChildren()
  }

  setStatus(message, color = "#111827") {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.style.color = color
  }

  resetBall() {
    if (!this.hasBallTarget) return
    this.ballTarget.classList.remove("animate-roll")
    this.ballTarget.style.opacity = "1"
    this.ballTarget.style.visibility = "visible"
    this.ballTarget.style.pointerEvents = "auto"
  }

  resetWidget() {
    const widget = this.widgetElement()
    if (!widget || typeof turnstile === "undefined" || typeof turnstile.reset !== "function") return false
    try {
      turnstile.reset(this.widgetId ?? widget)
      return true
    } catch (error) {
      console.warn("❌ Turnstile reset failed:", error)
      return false
    }
  }

  refreshOnceIfNeeded() {
    if (!this.airdropFlow()) return
    const key = `pinball-turnstile-client-refresh:${this.currentPath()}`
    const last = Number(sessionStorage.getItem(key) || "0")
    const now = Date.now()
    if (now - last < 20_000) return
    sessionStorage.setItem(key, String(now))
    this.cancelRefresh()
    this.refreshTimer = window.setTimeout(() => window.location.reload(), 3000)
  }

  cancelRefresh() {
    window.clearTimeout(this.refreshTimer)
    this.refreshTimer = null
  }

  scheduleTurnstileRender(delay = 300) {
    if (this.renderTimer) window.clearTimeout(this.renderTimer)
    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null
      this.ensureTurnstileRenders()
    }, delay)
  }

  ensureTurnstileRenders() {
    const widget = this.widgetElement()
    if (!widget) {
      console.warn("❌ .cf-turnstile not found inside controller.")
      return
    }

    if (this.widgetId !== null) {
      console.log("✅ Skipping manual Turnstile render — already rendered.")
      return
    }

    if (typeof turnstile === "undefined") {
      this.renderRetryCount += 1
      if (this.renderRetryCount === 10) {
        this.setStatus("⏳ Cloudflare is still loading. Keeping the check alive...", "#b45309")
      }
      this.scheduleTurnstileRender(Math.min(250 + (this.renderRetryCount * 50), 1_000))
      return
    }

    if (typeof turnstile.render !== "function") {
      this.scheduleTurnstileRender(500)
      return
    }

    if (widget.dataset.pinballRenderPending === "true") return

    widget.dataset.pinballRenderPending = "true"
    try {
      this.widgetId = turnstile.render(widget, {
        sitekey: widget.dataset.sitekey,
        "response-field": false,
        callback: this.boundTurnstileSuccess,
        "expired-callback": this.boundTurnstileExpired,
        "error-callback": this.boundTurnstileError
      })
      widget.dataset.rendered = "true"
      this.renderRetryCount = 0
    } catch (error) {
      console.warn("❌ Turnstile render delayed:", error)
      widget.replaceChildren()
      delete widget.dataset.rendered
      this.renderRetryCount += 1
      this.scheduleTurnstileRender(Math.min(300 + (this.renderRetryCount * 50), 1_000))
    } finally {
      delete widget.dataset.pinballRenderPending
    }
  }

  onSuccess(token) {
    this.cancelRefresh()
    console.log("✅ Turnstile verified")

    const form = this.currentForm()
    const hiddenInForm = form ? form.querySelectorAll('input[name="cf-turnstile-response"]') : []
    hiddenInForm.forEach((input) => { input.value = token })

    this.element.classList.add("turnstile-verified")
    this.setStatus("👾 Thank you human, you may proceed!")

    if (this.hasBallTarget) {
      this.ballTarget.classList.add("animate-roll")
      this.ballTarget.style.opacity = "1"
      this.ballTarget.style.visibility = "visible"
      this.ballTarget.style.pointerEvents = "auto"
    }

    this.element.dispatchEvent(new CustomEvent("turnstile:success", {
      bubbles: true,
      detail: { token }
    }))
  }

  onExpired() {
    console.warn("⚠️ Turnstile expired.")
    this.element.classList.remove("turnstile-verified")
    this.resetHiddenTokens()
    this.resetBall()
    this.setStatus("⏳ Cloudflare check expired. Resetting now...", "#b45309")
    if (!this.resetWidget()) this.refreshOnceIfNeeded()
  }

  onError(code) {
    console.warn("❌ Turnstile browser error:", code)
    this.element.classList.remove("turnstile-verified")
    this.resetHiddenTokens()
    this.resetBall()
    this.setStatus("❌ Browser or network error on the Cloudflare check. Resetting now...", "#991b1b")
    if (!this.resetWidget()) this.refreshOnceIfNeeded()
  }

}
